import React, { forwardRef, useRef, useImperativeHandle, useEffect } from 'react';

export default forwardRef(function GetPaginaSeis(props, ref) {
  const { logoSrc } = props || {};
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);

  const draw = (opts = {}) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const wrapper = wrapperRef.current;
      if (!canvas) return resolve();

      const containerWidth = wrapper ? wrapper.clientWidth : 800;
      const width = Math.max(600, containerWidth);
      const height = Math.round(width * (8.5 / 11));
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, width, height);

      // bg
      ctx.fillStyle = opts.bgColor || props.bgColor || '#ffffff';
      ctx.fillRect(0, 0, width, height);

      // layout paddings
      const padding = 24;
      let y = padding;

      // title square + title (1.3)
      const titleFontSize = opts.titleFontSize || 40;
      const squareSize = typeof opts.leftSquareSize === 'number' ? opts.leftSquareSize : 70;
      const squareGap = typeof opts.titleSquareGap === 'number' ? opts.titleSquareGap : 18;
      const squareText = opts.titleSquareText || '1.3';

      // square
      ctx.fillStyle = opts.titleSquareBg || '#5fe7ffff';
      ctx.fillRect(padding, y, squareSize, squareSize);

      ctx.fillStyle = opts.titleSquareColor || '#01090cff';
      ctx.font = `bold ${opts.leftSquareFontSize || 40}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(squareText, padding + squareSize / 2, y + squareSize / 2);

      // title (wrapped/justified spanning full width)
      const title = opts.title || props.title || '';
      const titleX = padding + squareSize + squareGap;
      const titleMaxW = width - titleX - padding;
      ctx.fillStyle = opts.titleColor || '#111827';
      ctx.font = `bold ${titleFontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';

      const justifyAndWrap = (c, text, x, y0, maxW, lineH) => {
        const words = String(text).split(/\s+/).filter(Boolean);
        const lines = [];
        let cur = [];
        let curW = 0;
        const spaceW = c.measureText(' ').width;
        for (let w of words) {
          const ww = c.measureText(w).width;
          if (cur.length === 0) {
            cur.push(w); curW = ww;
          } else if (curW + spaceW + ww <= maxW) {
            cur.push(w); curW += spaceW + ww;
          } else {
            lines.push({ words: cur.slice(), wordsWidth: curW });
            cur = [w]; curW = ww;
          }
        }
        if (cur.length) lines.push({ words: cur.slice(), wordsWidth: curW });

        let y = y0;
        for (let i = 0; i < lines.length; i++) {
          const ln = lines[i];
          if (i < lines.length - 1 && ln.words.length > 1) {
            // justify
            const gaps = ln.words.length - 1;
            const extra = (maxW - ln.wordsWidth) / gaps;
            let cx = x;
            for (let j = 0; j < ln.words.length; j++) {
              c.fillText(ln.words[j], cx, y);
              cx += c.measureText(ln.words[j]).width + spaceW + extra;
            }
          } else {
            c.fillText(ln.words.join(' '), x, y);
          }
          y += lineH;
        }
        return y;
      };

      const titleLineH = Math.round(titleFontSize * 1.2);
      const afterTitleY = justifyAndWrap(ctx, title, titleX, y, titleMaxW, titleLineH);

      // subtitle under title
      const subTitle = opts.subTitle || props.subTitle || '';
      if (subTitle) {
        const subFontSize = typeof opts.subTitleFontSize === 'number' ? opts.subTitleFontSize : 30;
        ctx.font = `bold ${subFontSize}px Inter, Arial, sans-serif`;
        ctx.fillStyle = opts.subTitleColor || '#475569';
        const subLineH = Math.round(subFontSize * 1.2);
        const afterSubY = justifyAndWrap(ctx, subTitle, titleX, afterTitleY + 8, titleMaxW, subLineH);
        y = Math.max(y + squareSize, afterSubY) + 24;
      } else {
        y = Math.max(y + squareSize, afterTitleY) + 24;
      }

      // draw two charts on the right half: graficaUno (pie) and graficaDos (bars)
      const graficaUnoData = opts.graficaUnoData || props.graficaUnoData || [];
      const graficaDosData = opts.graficaDosData || props.graficaDosData || [];

      const rightAreaX = Math.round(width * 0.48);
      const rightAreaW = width - rightAreaX - padding;
      const chartGap = 24;
      // start charts a fixed distance below the subtitle/title block (200px by default)
      const chartsTopGap = typeof opts.chartsTopGap === 'number' ? opts.chartsTopGap : 50;
      const chartsStartY = y + chartsTopGap;

      // layout: graficaUno (pie) on LEFT area, graficaDos (bars) on RIGHT area
      const leftAreaX = padding;
      const leftAreaW = rightAreaX - leftAreaX - 8; // small gap between left and center

      // graficaUno: simple donut/pie centered in left top area
      // compute radius first then place pie so its TOP aligns with chartsStartY (same top as bars)
      const maxPieByWidth = Math.max(0, leftAreaW - 8); // leave small horizontal padding
      const maxPieByHeight = Math.max(0, height - chartsStartY - padding - 120); // vertical space available
      const pieDiameter = Math.max(0, Math.min(600, maxPieByWidth, maxPieByHeight));
      const pieR = Math.max(10, Math.round(pieDiameter / 2));
      // center X as before, center Y offset so top of pie = chartsStartY (centerY = chartsStartY + radius)
      const pieX = leftAreaX + Math.round(leftAreaW / 2);
      const pieY = chartsStartY + pieR;
      const totalVal = graficaUnoData.reduce((s, it) => s + (Number(it.valor) || 0), 0) || 1;
      let ang = -Math.PI / 2;
      const palette = opts.palette || ['#f59e0b', '#06b6d4', '#e7d488ff', '#10b981', '#6366f1', '#a78bfa'];
      // helper: decide contrasting text color (black/white) for a hex color
      const contrastFor = (hex) => {
        try {
          const h = hex.replace('#','');
          const r = parseInt(h.length===3 ? h[0]+h[0] : h.slice(0,2),16);
          const g = parseInt(h.length===3 ? h[1]+h[1] : h.slice(2,4),16);
          const b = parseInt(h.length===3 ? h[2]+h[2] : h.slice(4,6),16);
          const lum = 0.299*r + 0.587*g + 0.114*b;
          return lum < 140 ? '#0f172a' : '#0f172a';
        } catch (e) { return '#0f172a'; }
      };
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        const v = Number(it.valor) || 0;
        const a = (v / totalVal) * Math.PI * 2;
        const startAng = ang;
        const endAng = ang + a;

        // slice
        ctx.beginPath();
        ctx.moveTo(pieX, pieY);
        const fillColor = palette[i % palette.length];
        ctx.fillStyle = fillColor;
        ctx.arc(pieX, pieY, pieR, startAng, endAng);
        ctx.lineTo(pieX, pieY);
        ctx.fill();

        // label on slice: value and percentage, positioned inside slice
        const mid = startAng + a / 2;
        const labelRadius = Math.max(12, Math.round(pieR * 0.62)); // inside slice
        const lx = pieX + Math.cos(mid) * labelRadius;
        const ly = pieY + Math.sin(mid) * labelRadius;
        const pct = Math.round((v / totalVal) * 1000) / 10; // one decimal
        const labelText = (Number.isFinite(v) ? String(v) : '') + (typeof v === 'number' ? ` (${pct}%)` : '');

        ctx.save();
        const baseLabelSize = Math.max(10, Math.round(pieR * 0.12));
        ctx.font = `${baseLabelSize}px Inter, Arial, sans-serif`;
        const maxTextWidth = Math.max(20, Math.round(labelRadius * a * 0.9));
        const textWidth = ctx.measureText(labelText).width;
        const canFitInside = a > 0.12 && textWidth <= maxTextWidth;
        ctx.textBaseline = 'middle';
        if (canFitInside) {
          ctx.textAlign = 'center';
          ctx.fillStyle = contrastFor(fillColor);
          ctx.fillText(labelText, lx, ly);
        } else {
          // keep font size; move label outside the pie if it cannot fit inside
          const outsideR = pieR + 14;
          const ox = pieX + Math.cos(mid) * outsideR;
          const oy = pieY + Math.sin(mid) * outsideR;
          const alignRight = Math.cos(mid) < 0;
          ctx.textAlign = alignRight ? 'right' : 'left';
          ctx.fillStyle = opts.pieOutsideLabelColor || '#0f172a';
          ctx.strokeStyle = opts.pieOutsideLineColor || 'rgba(15,23,42,0.35)';
          ctx.lineWidth = 1;
          const edgeX = pieX + Math.cos(mid) * (pieR - 1);
          const edgeY = pieY + Math.sin(mid) * (pieR - 1);
          const elbowX = pieX + Math.cos(mid) * (pieR + 6);
          const elbowY = pieY + Math.sin(mid) * (pieR + 6);
          ctx.beginPath();
          ctx.moveTo(edgeX, edgeY);
          ctx.lineTo(elbowX, elbowY);
          ctx.lineTo(ox, oy);
          ctx.stroke();
          ctx.fillText(labelText, ox + (alignRight ? -4 : 4), oy);
        }
        ctx.restore();

        ang += a;
      }
      // inner hole (optional) - default is full pie (no hole)
      const innerRadius = typeof opts.pieInnerRadius === 'number' ? opts.pieInnerRadius : 0;
      if (innerRadius > 0) {
        ctx.fillStyle = opts.pieInnerBg || '#ffffff';
        ctx.beginPath();
        ctx.arc(pieX, pieY, Math.round(innerRadius), 0, Math.PI * 2);
        ctx.fill();
      }

      // legend under pie (left-aligned to pie)
      ctx.font = `${opts.legendFontSize || 14}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      let legendY = pieY + pieR + 8;
      const legendX = leftAreaX + 8;
      for (let i = 0; i < graficaUnoData.length; i++) {
        const it = graficaUnoData[i];
        ctx.fillStyle = palette[i % palette.length];
        ctx.fillRect(legendX, legendY, 12, 12);
        ctx.fillStyle = opts.legendColor || '#111827';
        ctx.fillText(`${it.categoria} (${it.valor})`, legendX + 18, legendY - 2);
        legendY += 18;
      }

      // graficaDos: vertical bars on the RIGHT area
      const barsX = rightAreaX + 8;
      const barsW = rightAreaW - 16;
      // set bar chart top to the pie center Y so graphic dos is lineal with graphic uno
      // (uses pieY computed just above)
      const barsTopY = pieY;
      const vbarGap = typeof opts.vbarGap === 'number' ? opts.vbarGap : 16;
      const nBars = Math.max(1, graficaDosData.length);
      const maxBarVal = graficaDosData.reduce((m, it) => Math.max(m, Number(it.valor) || 0), 0) || 1;
      // compute per-bar slot width
      const totalGap = (nBars - 1) * vbarGap;
      const slotW = Math.floor((barsW - totalGap) / nBars);
      const vBarMaxH = Math.min(220, height - barsTopY - padding - 120); // max bar height

      ctx.font = `${opts.barLabelSize || 20}px Inter, Arial, sans-serif`;
      for (let i = 0; i < nBars; i++) {
        const it = graficaDosData[i] || {};
        const bx = barsX + i * (slotW + vbarGap);
        // bar background (full slot)
        ctx.fillStyle = opts.barBg || '#e6e7ea';
        const barBgX = bx;
        const barBgY = barsTopY;
        ctx.fillRect(barBgX, barBgY, slotW, vBarMaxH);

        // value -> bar height
        const val = Number(it.valor) || 0;
        const h = Math.round((val / maxBarVal) * vBarMaxH);
        const barFillX = bx;
        const barFillY = barsTopY + (vBarMaxH - h);
        const barFillColor = palette[i % palette.length];
        ctx.fillStyle = barFillColor;
        ctx.fillRect(barFillX, barFillY, slotW, h);

        // value text above bar (or inside if tall enough)
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const valY = barFillY - 6;
        ctx.font = `bold ${Math.max(12, (opts.barValueFontSize || 30))}px Inter, Arial, sans-serif`;
        ctx.fillText(String(it.valor || ''), bx + Math.round(slotW / 2), valY);

        // label under bar (department/category) - display both with wrapping
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.font = `${opts.barLabelSize || 12}px Inter, Arial, sans-serif`;
        const label = String(`${it.departamento || ''}`.trim());
        const label2 = String(`${it.categoria || ''}`.trim());
        const labelY = barsTopY + vBarMaxH + 8;
        const maxLabelW = slotW;
        const lineHeight = Math.round((opts.barLabelSize || 12) * 1.3);
        
        // wrap and display label (departamento)
        const wrapLabel = (text) => {
          const words = text.split(/\s+/);
          const lines = [];
          let currentLine = '';
          for (let w of words) {
            const testLine = currentLine ? currentLine + ' ' + w : w;
            if (ctx.measureText(testLine).width <= maxLabelW) {
              currentLine = testLine;
            } else {
              if (currentLine) lines.push(currentLine);
              currentLine = w;
            }
          }
          if (currentLine) lines.push(currentLine);
          return lines;
        };
        
        let currentY = labelY;
        
        // draw label lines (departamento)
        if (label) {
          const labelLines = wrapLabel(label);
          for (let li = 0; li < labelLines.length; li++) {
            ctx.fillText(labelLines[li], bx + Math.round(slotW / 2), currentY);
            currentY += lineHeight;
          }
        }
        
        // draw label2 lines (categoria) below label
        if (label2) {
          const label2Lines = wrapLabel(label2);
          for (let li = 0; li < label2Lines.length; li++) {
            ctx.fillText(label2Lines[li], bx + Math.round(slotW / 2), currentY);
            currentY += lineHeight;
          }
        }
      }

      // bottom texts: place textoUno under graficaUno (left) and textoDos under graficaDos (right), then footer
      const textoUno = opts.textoUno || props.textoUno || '';
      const textoDos = opts.textoDos || props.textoDos || '';
      const footerText = opts.footerText || props.footerText || '';
      ctx.font = `${opts.bottomFontSize || 24}px Inter, Arial, sans-serif`;
      ctx.fillStyle = opts.bottomColor || '#374151';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      const drawSmallWrap = (c, text, x, y0, maxW, lh) => {
        const words = String(text).split(' ');
        let line = '';
        let yPos = y0;
        for (let n = 0; n < words.length; n++) {
          const test = line ? line + ' ' + words[n] : words[n];
          if (c.measureText(test).width > maxW && line) {
            c.fillText(line, x, yPos);
            line = words[n];
            yPos += lh;
          } else {
            line = test;
          }
        }
        if (line) c.fillText(line, x, yPos);
        return yPos + lh;
      };

      // textoUno under the pie/legend (left area)
      const leftTextX = leftAreaX + 8;
      const leftTextMaxW = leftAreaW - 16;
      const leftTextY = legendY + 50;
      drawSmallWrap(ctx, textoUno, leftTextX, leftTextY, leftTextMaxW, 20);

      // textoDos under the vertical bars (right area)
      const rightTextX = barsX;
      const rightTextMaxW = barsW - 8;
      const rightTextY = barsTopY + vBarMaxH + 200;
      drawSmallWrap(ctx, textoDos, rightTextX, rightTextY, rightTextMaxW, 20);

      // footer (muted) at page bottom
      ctx.fillStyle = opts.footerColor || '#6b7280';
      ctx.font = `${opts.footerFontSize || 20}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(footerText, padding, height - padding - 6);

      // optional logo bottom-right
      const mainLogoSrc = opts.logoSrc || logoSrc;
      if (mainLogoSrc) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          try {
            const maxW = Math.min(240, Math.round(width * 0.22));
            const w = Math.min(maxW, img.width);
            const h = (img.height / img.width) * w;
            const lx = width - padding - w;
            const ly = height - padding - h;
            ctx.drawImage(img, lx, ly, w, h);
          } catch (e) { /* ignore */ }
          resolve({ canvas, width, height });
        };
        img.onerror = () => resolve({ canvas, width, height });
        img.src = mainLogoSrc;
      } else {
        resolve({ canvas, width, height });
      }
    });
  };

  const download = (filename) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = filename || `pagina_seis_${new Date().toISOString().slice(0,10)}.png`;
    a.click();
  };

  useImperativeHandle(ref, () => ({
    draw,
    download,
    getDataUrl: () => {
      const canvas = canvasRef.current;
      return canvas ? canvas.toDataURL('image/png') : null;
    }
  }), [draw]);

  useEffect(() => {
    draw();
    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <canvas ref={canvasRef} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
    </div>
  );
});